import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
   static targets = ["button", "list"]

   connect() {
      this.open = false;

      this.handleResize = this.handleResize.bind(this);
      window.addEventListener('resize', this.handleResize);

      if (this.hasListTarget) {
         this.listTarget.addEventListener('keydown', this.onListKeyDown.bind(this));
      }
      if (this.hasButtonTarget) {
         this.buttonTarget.addEventListener('click', this.toggle.bind(this));
         this.buttonTarget.addEventListener('keydown', this.onButtonKeyDown.bind(this));
      }
      if (this.hasListTarget && this.hasButtonTarget) {
         this.buttonTarget.setAttribute('aria-controls', this.listTarget.id);
      }

      this.element.addEventListener('focusout', this.onBlur.bind(this));
      this.handleResize();
   }

   disconnect() {
      // release events
      window.removeEventListener('resize', this.handleResize)
      this.element.removeEventListener('focusout', this.onBlur.bind(this));
      if (this.hasButtonTarget) {
         this.buttonTarget.removeEventListener('click', this.toggle.bind(this));
         this.buttonTarget.removeEventListener('keydown', this.onButtonKeyDown.bind(this));
      }
      if (this.hasListTarget) {
         this.listTarget.removeEventListener('keydown', this.onListKeyDown.bind(this));
      }
   }

   handleResize() {
      const containerIsVisible = this.element.offsetParent !== null;
      const buttonIsVisible = this.buttonTarget.offsetParent !== null;
      this.toggleExpand(containerIsVisible && !buttonIsVisible);
   }

   // Actions
   toggle() {
      const buttonExpanded = this.buttonTarget.getAttribute('aria-expanded') === 'true';
      this.toggleExpand(!buttonExpanded);
   }

   onBlur(event) {
      event.stopPropagation();
      const listContainsFocus = this.element.contains(event.relatedTarget);
      const buttonIsVisible = this.buttonTarget.offsetParent !== null;
      if (buttonIsVisible && !listContainsFocus && this.open) {
         this.toggleExpand(false);
      }
   }

   onButtonKeyDown(event) {
      if (event.key === 'Escape') {
         if (this.open) {
            event.stopPropagation();
         }
         this.toggleExpand(false);
      } else if (this.open && event.key === 'ArrowDown') {
         event.stopPropagation();
         this.listTarget.querySelector('a,button').focus();
      }
   }

   onListKeyDown(event) {
      if (!this.open) {
         return;
      }

      const buttonIsVisible = this.buttonTarget.offsetParent !== null;
      const menuLinks = Array.from(this.listTarget.querySelectorAll('a,button'));
      const currentIndex = menuLinks.indexOf(document.activeElement);

      if (buttonIsVisible && event.key === 'Escape') {
         event.stopPropagation();
         this.buttonTarget.focus();
         this.toggleExpand(false);
      }

      this.controlFocusByKey(event, menuLinks, currentIndex);
   }

   // Private methods
   toggleExpand(expanded) {
      this.open = expanded;

      if (this.hasButtonTarget) {
         this.buttonTarget.setAttribute('aria-expanded', expanded);
      }

      if (this.hasListTarget) {
         this.listTarget.style.display = expanded ? 'block' : 'none'
      }
   }

   controlFocusByKey(keyboardEvent, nodeList, currentIndex) {
      switch (keyboardEvent.key) {
         case 'ArrowUp':
         case 'ArrowLeft':
            keyboardEvent.preventDefault();
            keyboardEvent.stopPropagation();
            if (currentIndex > -1) {
               const prevIndex = Math.max(0, currentIndex - 1);
               nodeList[prevIndex].focus();
            }
            break;
         case 'ArrowDown':
         case 'ArrowRight':
            keyboardEvent.preventDefault();
            keyboardEvent.stopPropagation();
            if (currentIndex > -1) {
               const nextIndex = Math.min(nodeList.length - 1, currentIndex + 1);
               nodeList[nextIndex].focus();
            }
            break;
         case 'Home':
            keyboardEvent.preventDefault();
            keyboardEvent.stopPropagation();
            nodeList[0].focus();
            break;
         case 'End':
            keyboardEvent.preventDefault();
            keyboardEvent.stopPropagation();
            nodeList[nodeList.length - 1].focus();
            break;
      }
   }
}
